import React from 'react'
import { observer } from 'mobx-react-lite'

import Stack from '@mui/material/Stack'

import { useFinstrongStore, useGlobalCopyStore, useUserStore } from '../../common'
import { KeyIndicatorsTabItemType } from '../constants'
import { getCreditScoreState, getManageDebtRatioState } from '../util/finstrongUtils'

import KeyIndicatorsTabsItem from './KeyIndicatorsTabItem'
import { buildDynamicCopy } from '../../core/utils/Localization'

const KeyIndicatorsBorrowTab: React.FC = () => {
  const { healthScore } = useFinstrongStore()
  const { finstrong: copyContent } = useGlobalCopyStore()
  const { key_indicators_borrow: borrowCopy } = copyContent
  const { user } = useUserStore()

  const getManageDebtRatioStatus = (ratio: number | null) => {
    const state = getManageDebtRatioState(ratio)

    const cardStatusMapping = {
      GOOD: borrowCopy.manage_debit_good_description,
      FAIR: borrowCopy.manage_debit_fair_description,
      OFF_TRACK: borrowCopy.manage_debit_off_track_description,
      MISSING: borrowCopy.manage_debit_missing_description,
    }

    const description =
      state === 'FAIR' || state === 'OFF_TRACK'
        ? (buildDynamicCopy(cardStatusMapping[state], ratio) as string)
        : cardStatusMapping[state]

    return {
      cardState: state,
      description,
      title: borrowCopy.manage_debit_title,
    }
  }

  const getCreditScoreStatus = (score: number | null) => {
    const state = getCreditScoreState(score)

    const cardStatusMapping = {
      GOOD: borrowCopy.credit_score_good_description,
      FAIR: borrowCopy.credit_score_fair_description,
      OFF_TRACK: borrowCopy.credit_score_off_track_description,
      MISSING: borrowCopy.credit_score_missing_description,
    } as const

    const description =
      state === 'MISSING'
        ? cardStatusMapping[state]
        : (buildDynamicCopy(cardStatusMapping[state], score) as string)

    return {
      cardState: state,
      description,
      title: borrowCopy.credit_score_title,
    }
  }

  const { manageDebtStatus, creditScoreStatus } = React.useMemo(() => {
    const convertedDebtToIncomeRatio =
      healthScore.debt_to_income_ratio !== null
        ? Math.round(healthScore.debt_to_income_ratio * 100)
        : null
    const manageDebtStatus = getManageDebtRatioStatus(convertedDebtToIncomeRatio)
    const creditScoreStatus = getCreditScoreStatus(user.credit_score || null)

    return {
      manageDebtStatus,
      creditScoreStatus,
    }
  }, [healthScore.debt_to_income_ratio, user.credit_score, borrowCopy, copyContent])

  return (
    <Stack direction="column" gap={24} sx={{ p: 8, pt: 24 }}>
      <KeyIndicatorsTabsItem
        cardState={manageDebtStatus.cardState}
        description={manageDebtStatus.description}
        drawerFor={KeyIndicatorsTabItemType.HaveManageableDebt}
        title={manageDebtStatus.title}
      />
      <KeyIndicatorsTabsItem
        cardState={creditScoreStatus.cardState}
        description={creditScoreStatus.description}
        drawerFor={KeyIndicatorsTabItemType.HaveAGoodCreditScore}
        title={creditScoreStatus.title}
      />
    </Stack>
  )
}

export default observer(KeyIndicatorsBorrowTab)
